import * as Sentry from '@sentry/react-router'
import { HydratedRouter } from 'react-router/dom'
import { startTransition, StrictMode } from 'react'
import { hydrateRoot } from 'react-dom/client'
import { reloadStaleBundle } from '~/utils/reload-stale-bundle'

Sentry.init({
  dsn: 'https://21962f49cec3b6388e7c8e8ca78b3c9a@o4505444271783936.ingest.us.sentry.io/4506650981105664',
  tracesSampleRate: 0.01,
  // Matched against the message and the exception value. These arrive as
  // frameless promise rejections, so the stacktrace check below cannot see them.
  ignoreErrors: [
    // Wistia player media and image failures.
    'The source image cannot be decoded',
    'EncodingError: Loading was canceled',
    'EncodingError: Loading error',
    'EncodingError: Invalid image request',
    'The element has no supported sources',
    '[object MediaError]',
    // A stale bundle after a deploy, or a dropped mobile connection.
    'Importing a module script failed',
    'Failed to fetch dynamically imported module',
    'error loading dynamically imported module',
    // GTM-injected pixel we neither ship nor control.
    'api.vector.co',
    // A GTM-injected tag failing to load, usually because a content blocker
    // stopped it. The rejection value is the script element's own error event,
    // so it carries no message or stack.
    /^Event `\w+` \(type=error\) captured as promise rejection$/,
    // Browser extensions and embedded webviews.
    'Invalid call to runtime.sendMessage()',
    'Error invoking postMessage: Java object is gone',
    // Browsers too old to run the site at all.
    'Object.hasOwn is not a function',
  ],
  beforeSend(event) {
    if (
      event.exception?.values?.some(
        (e) =>
          e.type === 'UnhandledRejection' &&
          e.value?.includes('Non-Error promise rejection captured with value:'),
      )
    )
      return null

    // Drop errors originating from Wistia's video player
    if (
      event.exception?.values?.some((e) =>
        e.stacktrace?.frames?.some(
          (f) =>
            f.filename?.includes('wistia') ||
            f.filename?.includes('publicApi.js'),
        ),
      )
    )
      return null

    return event
  },
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.thirdPartyErrorFilterIntegration({
      behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
      filterKeys: ['omni-website-js'],
    }),
  ],
})

window.addEventListener('vite:preloadError', reloadStaleBundle)

startTransition(() => {
  hydrateRoot(
    document,
    <StrictMode>
      <HydratedRouter />
    </StrictMode>,
  )
})
